import Usercentrics, {
  UserDecision,
  TCF_DECISION_UI_LAYER,
  TCFUserDecisions,
  TCFData,
  TCFPurpose,
  TCFSpecialFeature,
  TCFVendor,
  API_COMMANDS,
} from '@usercentrics/cmp-browser-sdk'

import { isDevMode } from '@sportworld/js-sdk/src/envs/envs'

import { getPrivacyRegulation } from '@/actions/region'
import { eventObject, eventAction } from '@/constants/analytics'
import { consentLoadedStatus, fetchStatus } from '@/constants/common'
import i18n, { getCurrentLanguage } from '@/i18n'
import { getPlatformName, Keys, reloadApp } from '@/platform/platform'
import useStore from '@/store/useStore'
import { Labels } from '@/types'
import { sendAnalyticsEvent } from '@/utils/analyticsUtils'
import { showError, showInfo } from '@/utils/utils'

let UC: Nullable<Usercentrics> = null
let ucDetails = {}

if (isDevMode) {
  window.UC = UC
}

const consentLoaded = (ucDetails: any) => {
  sendAnalyticsEvent({
    object: eventObject.CONSENT,
    action: eventAction.LOADED,
    consentSystem: 'usercentrics',
    ucDetails,
  })
}

export const consentShown = () => {
  sendAnalyticsEvent({
    object: eventObject.CONSENT,
    action: eventAction.OPENED,

    consentSystem: 'usercentrics',
    ucDetails,
  })
}

const getPlatformNameDetails = () => {
  const type = getPlatformName()
  switch (type) {
    case 'tizen':
      return 'samsungDetails'
    case 'webos':
      return 'lgDetails'
    case 'fireos':
      return 'amazonDetails'
    default:
      return 'browserDetails'
  }
}

const consentSelected = (ucDetails: any) => {
  const detailsProp = getPlatformNameDetails()
  sendAnalyticsEvent({
    object: eventObject.CONSENT,
    action: eventAction.CHANGED,
    consentSystem: 'usercentrics',
    ucDetails,
    [detailsProp]: {
      marketing: ucDetails.ucCategory.marketing,
      statistic: ucDetails.ucCategory.functional,
    },
  })
}
const { setConsentStatus } = useStore.getState().common

export const addConsentListener = async () => {
  window.addEventListener('consentChanged', function (e: any) {
    const { action, type } = e.detail || {}
    if (!action) {
      return
    }
    switch (action) {
      case 'onInitialPageLoad':
        ucDetails = e.detail
        consentLoaded(e.detail)
        if (type.toLowerCase() === 'explicit') {
          setConsentStatus(fetchStatus.COMPLETED)
        }
        break
      case 'onAcceptAllServices':
      case 'onDenyAllServices':
      case 'onUpdateServices':
        ucDetails = e.detail
        consentSelected(e.detail)
        setConsentStatus(fetchStatus.COMPLETED)
        break
    }
  })
}
addConsentListener()

export const loadUsercentrics = (userCentricsId: string) => {
  const privacyRegulation = getPrivacyRegulation()
  if (!privacyRegulation || privacyRegulation === 'None') {
    return setConsentStatus(consentLoadedStatus.COMPLETED)
  }

  ;(window as any).UC_TV_KEYMAP = {
    left: [37],
    right: [39],
    up: [38],
    down: [40],
    ok: [13],
    back: [Keys.BACK],
  }

  window.addEventListener('UC_UI_INITIALIZED', async () => {
    UC = (window as any).UC_UI
    const isConsentRequired = await (window as any).UC_UI?.isConsentRequired()
    if (isConsentRequired === false) {
      setConsentStatus(consentLoadedStatus.COMPLETED)
    }
  })

  window.addEventListener('UC_UI_CMP_EVENT', async (e: any) => {
    const { type } = e.detail || {}
    if (type === 'CMP_SHOWN') {
      consentShown()
      const isConsentRequired = await (window as any).UC_UI?.isConsentRequired()
      isConsentRequired !== false &&
        setConsentStatus(consentLoadedStatus.INITIALIZED)
      return
    }
    const completedTypes = ['ACCEPT_ALL', 'DENY_ALL', 'SAVE']
    if (type && completedTypes.includes(type)) {
      setConsentStatus(consentLoadedStatus.COMPLETED)
    }
  })

  const isTcfEnabled = privacyRegulation === 'Tcf'
  const script = document.createElement('script')
  script.src = 'https://web.cmp.usercentrics.eu/ui/loader.js'
  script.id = 'usercentrics-cmp'
  script.dataset.theme = 'tv'
  script.dataset.settingsId = userCentricsId
  isTcfEnabled && (script.dataset.tcfEnabled = 'true')
  script.dataset.language = i18n.language?.replace('-', '_')
  script.onerror = () => {
    console.error('Failed to load Usercentrics script')
    setConsentStatus(consentLoadedStatus.COMPLETED)
  }
  script.onload = () => {
    setConsentStatus(consentLoadedStatus.LOADED)
  }
  document.body.appendChild(script)
}

export const ucInit = async (userCentricsId: string) => {
  UC = new Usercentrics(userCentricsId, {
    createTcfApiStub: true,
  })
  return await UC.init()
    .then(async () => {
      const settings = UC?.getSettingsCore()
      const currentBrowserLanguage = getCurrentLanguage()

      isDevMode && console.log('[Usercentrics] init success')

      if (settings?.language.selected !== currentBrowserLanguage) {
        await UC?.changeLanguage(currentBrowserLanguage).catch(
          () =>
            isDevMode && console.error('[Usercentrics] changeLanguage error'),
        )
      }
    })
    .catch((e) => {
      console.error('[Usercentrics] init error', e)
      Promise.reject(e)
    })
}

export const getSettingsLabels = () => {
  const labels = UC?.getSettingsLabels() as Labels

  const settingsLabels = {
    buttons: labels.buttons,
    firstLayer: labels.firstLayer,
    secondLayer: labels.secondLayer,
    poweredBy: labels.poweredBy,
  }
  return settingsLabels
}

export const getServicesFullInfo = async () => {
  return await UC?.getServicesFullInfo()
}

export const getServicesBaseInfo = () => {
  return UC?.getServicesBaseInfo()
}

export const getServiceDecision = (key: string) => {
  const servicesBaseInfo = getServicesBaseInfo()
  const service = servicesBaseInfo?.find((el) => el.name === key)
  return service?.consent.status
}

export const getTCFVendorsConsent = (key: string) => {
  const tcfData = UC?.getTCFData()
  const tcfVendor = tcfData?.vendors.find((el) => el.name === key)
  return tcfVendor?.consent
}

export const getCategoriesFullInfo = async () => {
  return await UC?.getCategoriesFullInfo()
}

export const getTcfData = () => {
  return UC?.getTCFData()
}

export const getUSPData = () => {
  let res = ''
  try {
    window.__uspapi(API_COMMANDS.GET_USP_DATA, 1, (uspData, success) => {
      res = success ? uspData?.uspString ?? '' : ''
    })
  } catch (error) {
    console.error('[Usercentrics] getUSPData error', error)
  }
  return res
}

/**
 * @description
 * The Usercentrics CMP exposes the IAB TCF 2.2 API which offers a standardised way for publishers and vendors to fetch the users consent information. IAB certified vendors are able to work with the TCF 2.2 API, which means in most cases no further modification or adjustments to vendor scripts are needed.
 *
 * This means vendors can request the consent state when the CMP is loaded using the __tcfapi function with the command getTCData.
 * @link https://usercentrics.com/docs/web/features/tcf2/service-integration/
 * @returns string
 */
export const getTcString = () => {
  let res = ''
  try {
    window.__tcfapi('getTCData', 2, (tcData: any, success: boolean) => {
      res = success && tcData?.tcString
    })
  } catch (error) {
    console.error('[Usercentrics] getTCData error', error)
  }
  return res
}

export const onAcceptAllHandler = async (fromLayer: TCF_DECISION_UI_LAYER) => {
  await UC?.acceptAllForTCF(fromLayer)
  return await UC?.acceptAllServices()
}

export const onDenyAllHandler = async (fromLayer: TCF_DECISION_UI_LAYER) => {
  await UC?.denyAllForTCF(fromLayer)
  return await UC?.denyAllServices()
}

export const updateServices = async (
  userDecisions: UserDecision[],
  tcfUserDecisions: TCFUserDecisions,
  fromLayer: TCF_DECISION_UI_LAYER,
) => {
  await UC?.updateChoicesForTCF(tcfUserDecisions, fromLayer)
  return await UC?.updateServices(userDecisions)
}

export const getUCServicesDecisions = () => {
  const servicesBaseInfo = getServicesBaseInfo()

  const servicesDecision = servicesBaseInfo?.map((service) => {
    return {
      serviceId: service.id,
      status: service.consent.status,
    }
  })
  return servicesDecision
}

export const getUCTcfUserDecisions = () => {
  const tcfData = getTcfData()
  if (!tcfData) {
    return {}
  }
  return reduceTcfData(tcfData)
}

export const clearUCStorage = async () => {
  UC?.clearStorage()
    .then(() => {
      showInfo('Usercentrics storage has been cleared')
      reloadApp()
    })
    .catch((e) => {
      console.error('[Usercentrics] Clearing storage error', e)
      showError('Clearing storage error')
    })
}

export const reduceTcfData = (tcfData: TCFData) => {
  const reduceData = (
    data: TCFPurpose[] | TCFSpecialFeature[] | TCFVendor[],
  ) => {
    return data.map((obj) => {
      const {
        id,
        consent,
        legitimateInterestConsent,
        showConsentToggle,
        showLegitimateInterestToggle,
        showLegitimateInterestConsentToggle,
      } = obj as TCFVendor & TCFSpecialFeature & TCFPurpose
      const hasLegitimateInterestToggle =
        showLegitimateInterestToggle || showLegitimateInterestConsentToggle
      return {
        id,
        ...(showConsentToggle && { consent }),
        ...(hasLegitimateInterestToggle && {
          legitimateInterestConsent:
            legitimateInterestConsent === null || legitimateInterestConsent,
        }),
      }
    })
  }

  return {
    purposes: reduceData(tcfData.purposes),
    specialFeatures: reduceData(tcfData.specialFeatures),
    vendors: reduceData(tcfData.vendors),
  } as TCFUserDecisions
}

export const showUCBanner = () => {
  return (window as any).UC_UI?.showFirstLayer()
}
